import { useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { waitForBillingSetup } from "../utils/billingSetup";
import { parseBillingSetupReturn } from "../utils/billingReturn";

function cleanReturnPath(returnPath) {
  const [path, query = ""] = returnPath.split("?");
  const params = new URLSearchParams(query);
  params.delete("billing_setup");
  params.delete("session_id");
  // preserve shopId, resumeQuote, and other context params
  const qs = params.toString();
  return `${path}${qs ? `?${qs}` : ""}`;
}

/**
 * TASK-567: Stripe setup checkout return (dashboard).
 * Feature branch: sync DB before clearing URL (webhooks not wired).
 */
export function useBillingSetupReturn({
  mechanicId,
  shopId,
  returnPath = "/mechanic-dashboard",
  preserveShopId = false,
  skipBillingStatusFromApiRef,
  onStart,
  onComplete,
  onError,
  onProgress,
}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const handledRef = useRef(false);

  useEffect(() => {
    if (!mechanicId) return;

    const { isSuccessReturn, sessionId, shopId: urlShopId } =
      parseBillingSetupReturn(searchParams);
    if (!isSuccessReturn || !sessionId || handledRef.current) return;

    const effectiveShopId = urlShopId || shopId;
    if (!effectiveShopId) return;

    handledRef.current = true;
    if (skipBillingStatusFromApiRef) {
      skipBillingStatusFromApiRef.current = true;
    }
    onStart?.();

    const confirmSetup = async () => {
      const shopIdParam = searchParams.get("shopId");
      const basePath = returnPath.split("?")[0];
      const pathWithShop =
        preserveShopId && shopIdParam
          ? `${basePath}?shopId=${shopIdParam}`
          : returnPath;
      const cleanPath = cleanReturnPath(pathWithShop);

      try {
        const result = await waitForBillingSetup({
          mechanicId,
          shopId: effectiveShopId,
          sessionId,
          onProgress: (p) => onProgress?.(p?.message),
        });
        if (result.success && result.billingReadyForQuotes) {
          if (skipBillingStatusFromApiRef) {
            skipBillingStatusFromApiRef.current = false;
          }
          onComplete?.(result);
          navigate(cleanPath, { replace: true });
        } else {
          if (skipBillingStatusFromApiRef) {
            skipBillingStatusFromApiRef.current = false;
          }
          onError?.(new Error(result.error || "Could not confirm payment setup."));
        }
      } catch (err) {
        if (skipBillingStatusFromApiRef) {
          skipBillingStatusFromApiRef.current = false;
        }
        onError?.(err);
      }
    };

    confirmSetup();
  }, [
    mechanicId,
    shopId,
    searchParams,
    navigate,
    returnPath,
    preserveShopId,
    skipBillingStatusFromApiRef,
    onStart,
    onComplete,
    onError,
    onProgress,
  ]);
}
